import React, { useState } from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Inbox, Sparkles, FileText } from "lucide-react";

import { DocumentType } from "../../../../../api/caseDocuments";
import UploadDocumentModal from "../../../../../features/documentManagement/components/uploadDocumentModal";
import { fetchDocumentDownloadUrl } from "../../../../../thunks/caseDocument";
import { selectDocumentsByPartyIdAndType } from "../../../../../features/documentManagement/logic/caseDocumentsSelector";
import { useAppDispatch, useAppSelector } from "../../../../../app/store";
import { selectSCNResponseByPartyId, selectSCNResponseDefaultSelectionByPartyId } from "../logic/scnResponseReportsSlice";
import { showErrorToast } from "../../../../../lib/toast";
import { DocumentEntityType } from "../../../../../api/caseDocuments";

interface SCNResponseTabContainerProps {
  partyId: string;
  isLoading?: boolean;
  isSaving: boolean;
  onConfirmNoResponse?: (partyId: string) => void;
}

const SCNResponseTabContainer: React.FC<SCNResponseTabContainerProps> = ({
  partyId,
  isLoading,
  isSaving,
  onConfirmNoResponse,
}) => {
  const dispatch = useAppDispatch();

  const scnResponse = useAppSelector((state) =>
  selectSCNResponseByPartyId(state, partyId)
 );

 const hasSCNResponse = scnResponse?.is_response_received === true;

  const defaultSelection = useAppSelector((state) =>
  selectSCNResponseDefaultSelectionByPartyId(state, partyId)
 );

  const [selection, setSelection] = useState<"received" | "not-received" | "">("");
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  React.useEffect(() => {
    setSelection(defaultSelection);
  }, [partyId,defaultSelection]);

  const handleValueChange = (value: string) => {
    setSelection(value as "received" | "not-received");

    if (value === "not-received") {
      onConfirmNoResponse?.(partyId);
    }

    if (value === "received") {
      setIsUploadModalOpen(true);
    }
  };

   const scnResponseDocs = useAppSelector(
    selectDocumentsByPartyIdAndType(partyId, DocumentType.SCN_RESPONSE)
   );

  const handleViewResponse = async () => {
    if (!hasSCNResponse) return;

    const responseDocument = scnResponseDocs?.[0];

    if (!responseDocument) {
    showErrorToast("Response Document Unavailable");
    return;
    }

    try {
      const { download_url } = await dispatch(
        fetchDocumentDownloadUrl({
          document_id: responseDocument.document_id,
          params: {
            file_reference: responseDocument.file_reference,
            mode: "VIEW",
          },
        })
      ).unwrap();

      window.open(download_url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open SCN response:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading response…
      </div>
    );
  }


  return (
    <div className="flex h-full min-h-[calc(100vh-260px)] w-full justify-center pt-24">
      <div className="flex h-[267px] w-full max-w-[640px] flex-col items-center gap-6 rounded-xl border border-dashed border-gray-300 bg-white p-6">

        <div className="flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200 bg-white p-2 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            <Inbox className="h-6 w-6 text-gray-950" />
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-4">
          <span className="text-center text-xl font-semibold leading-7 text-[#0A0A0A]">
            Show Cause Notice Response Status
          </span>

          {hasSCNResponse  ? (
            <button
              onClick={handleViewResponse}
              className="flex items-center gap-2 rounded-md border border-[#3B82F6] px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
            >
              <FileText className="h-4 w-4" />
              View SCN Response
            </button>
          ) : (
            <RadioGroup.Root
              value={selection}
              onValueChange={handleValueChange}
              disabled={isSaving}
              className="flex gap-[6px]"
            >
              <div
                className={`flex items-start gap-3 rounded-lg border px-6 py-3 ${
                  selection === "received"
                    ? "border-blue-500 bg-blue-50"
                    : "border-[#E5E5E5] bg-white"
                }`}
              >
                <RadioGroup.Item
                  value="received"
                  className="flex h-4 w-4 items-center justify-center rounded-full border border-[#E5E5E5]"
                >
                  <RadioGroup.Indicator className="h-2 w-2 rounded-full bg-blue-500" />
                </RadioGroup.Item>
                <span className="text-[16px] font-medium leading-[16px] text-[#0A0A0A]">
                  Response received. Upload file
                </span>
              </div>

              <div
                className={`flex items-start gap-3 rounded-lg border px-6 py-3 ${
                  selection === "not-received"
                    ? "border-red-500 bg-red-500/10"
                    : "border-[#E5E5E5] bg-white"
                }`}
              >
                <RadioGroup.Item
                  value="not-received"
                  className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                    selection === "not-received"
                      ? "border-red-500 bg-[#EF4444]"
                      : "border-[#E5E5E5] bg-white"
                  }`}
                >
                  <RadioGroup.Indicator className="h-2 w-2 rounded-full bg-white" />
                </RadioGroup.Item>
                <span className="text-[16px] font-medium leading-[16px] text-[#0A0A0A]">
                  No response received
                </span>
              </div>
            </RadioGroup.Root>
          )}

          <div className="mt-6 flex items-center justify-center gap-1 rounded-[8px] bg-[#3B82F6] px-2 py-[2px]">
            <Sparkles className="h-3 w-3 text-[#FAFAFA]" />
            <span className="text-xs font-semibold leading-4 text-[#FAFAFA]">
              This information is key to generate the reasoned order.
            </span>
          </div>
        </div>

        {isUploadModalOpen && (
          <UploadDocumentModal
            onClose={() => setIsUploadModalOpen(false)}
            initialDocumentType={DocumentType.SCN_RESPONSE}
            initialEntityType={DocumentEntityType.PARTY_INVOLVED}
            initialEntityId={partyId}
            lockDocumentType
            lockEntity
          />
        )}
      </div>
    </div>
  );
};

export default SCNResponseTabContainer;
